import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Calendar, Clock, Check, ChevronLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useBooking } from "./BookingContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOT_TIMES = [];
for (let h = 9; h < 16; h++) {
  SLOT_TIMES.push(`${String(h).padStart(2, "0")}:00`);
  SLOT_TIMES.push(`${String(h).padStart(2, "0")}:30`);
}

const to12h = (t) => {
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${period}`;
};

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const upcomingDates = () => {
  const out = [];
  const cur = new Date();
  cur.setDate(cur.getDate() + 1); // start tomorrow
  while (out.length < 16) {
    const day = cur.getDay(); // 1=Mon .. 4=Thu
    if (day >= 1 && day <= 4) out.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};

export default function BookingModal() {
  const { open, closeBooking } = useBooking();
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [booked, setBooked] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const dates = useMemo(() => upcomingDates(), []);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStep(1);
        setDate(null);
        setTime(null);
        setBooked([]);
        setForm({ name: "", email: "", notes: "" });
        setConfirmed(null);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && closeBooking();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeBooking]);


  useEffect(() => {
    if (!date) return;
    axios
      .get(`${API}/availability`, { params: { date } })
      .then((r) => setBooked(r.data.unavailable || []))
      .catch(() => setBooked([]));
  }, [date]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const selectDate = (d) => {
    setDate(ymd(d));
    setTime(null);
    setStep(2);
  };

  const selectTime = (t) => {
    setTime(t);
    setStep(3);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, { ...form, date, time });
      setConfirmed(data);
      setStep(4);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const prettyDate = (dstr) => {
    if (!dstr) return "";
    const [y, m, d] = dstr.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  };

  return (

    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="booking-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0"
            style={{ backgroundColor: "rgba(46,36,29,0.55)", backdropFilter: "blur(6px)" }}
            onClick={closeBooking}
            data-testid="booking-backdrop"
          />
          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-xl rounded-3xl overflow-hidden shadow-2xl"
            style={{ backgroundColor: "var(--bg-primary)", border: "1px solid var(--border-light)" }}
          >
            <div
              className="px-7 pt-7 pb-5 flex items-start justify-between"
              style={{ backgroundColor: "var(--bg-secondary)" }}
            >
              <div>
                <p
                  className="text-xs tracking-[0.28em] uppercase mb-2"
                  style={{ color: "var(--rose-mocha-deep)" }}
                >
                  Book a discovery call
                </p>
                <h3
                  className="font-serif text-2xl md:text-3xl leading-tight"
                  style={{ color: "var(--text-primary)" }}
                >
                  A complimentary 30-minute call
                </h3>
                <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
                  Monday–Thursday · 9:00 AM–4:00 PM ET
                </p>
              </div>
              <button
                onClick={closeBooking}
                data-testid="booking-close-button"
                className="shrink-0 p-2 rounded-full transition-colors hover:bg-black/5"
                style={{ color: "var(--text-primary)" }}
              >
                <X size={22} />
              </button>
            </div>

            <div className="px-7 py-6 max-h-[62vh] overflow-y-auto">
              {step === 1 && (
                <div data-testid="booking-step-date">
                  <div className="flex items-center gap-2 mb-4" style={{ color: "var(--rose-mocha-deep)" }}>
                    <Calendar size={16} />
                    <span className="text-sm tracking-wide">Choose a day</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {dates.map((d) => (
                      <button
                        key={ymd(d)}
                        data-testid={`booking-date-${ymd(d)}`}
                        onClick={() => selectDate(d)}
                        className="rounded-2xl px-3 py-4 text-left transition-all duration-300 hover:-translate-y-0.5"
                        style={{
                          border: "1px solid var(--border-light)",
                          backgroundColor: "#fff",
                        }}
                      >
                        <span className="block text-xs uppercase tracking-wide" style={{ color: "var(--rose-mocha-deep)" }}>
                          {d.toLocaleDateString("en-US", { weekday: "short" })}
                        </span>
                        <span className="block font-serif text-xl" style={{ color: "var(--text-primary)" }}>
                          {d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div data-testid="booking-step-time">
                  <button
                    onClick={() => setStep(1)}
                    data-testid="booking-back-to-date"
                    className="flex items-center gap-1 text-sm mb-4"
                    style={{ color: "var(--rose-mocha-deep)" }}
                  >
                    <ChevronLeft size={16} /> {prettyDate(date)}
                  </button>
                  <div className="flex items-center gap-2 mb-4" style={{ color: "var(--rose-mocha-deep)" }}>
                    <Clock size={16} />
                    <span className="text-sm tracking-wide">Choose a time (ET)</span>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {SLOT_TIMES.map((t) => {
                      const taken = booked.includes(t);
                      return (
                        <button
                          key={t}
                          data-testid={`booking-time-${t}`}
                          disabled={taken}
                          onClick={() => selectTime(t)}
                          className="rounded-full px-2 py-2.5 text-sm transition-all duration-300 disabled:opacity-35 disabled:line-through hover:-translate-y-0.5"
                          style={{
                            border: "1px solid var(--border-light)",
                            backgroundColor: "#fff",
                            color: "var(--text-primary)",
                          }}
                        >
                          {to12h(t)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {step === 3 && (
                <form onSubmit={submit} data-testid="booking-step-details" className="flex flex-col gap-5">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    data-testid="booking-back-to-time"
                    className="flex items-center gap-1 text-sm self-start"
                    style={{ color: "var(--rose-mocha-deep)" }}
                  >
                    <ChevronLeft size={16} /> {prettyDate(date)} · {to12h(time)} ET
                  </button>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs tracking-[0.2em] uppercase" style={{ color: "var(--rose-mocha-deep)" }}>
                      Your name
                    </label>
                    <input
                      data-testid="booking-name-input"
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Jane Doe"
                      className="bg-transparent py-2.5 outline-none text-lg placeholder:opacity-40"
                      style={{ borderBottom: "1px solid var(--border-light)", color: "var(--text-primary)" }}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs tracking-[0.2em] uppercase" style={{ color: "var(--rose-mocha-deep)" }}>
                      Email address
                    </label>
                    <input
                      data-testid="booking-email-input"
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@company.com"
                      className="bg-transparent py-2.5 outline-none text-lg placeholder:opacity-40"
                      style={{ borderBottom: "1px solid var(--border-light)", color: "var(--text-primary)" }}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs tracking-[0.2em] uppercase" style={{ color: "var(--rose-mocha-deep)" }}>
                      Anything to share? (optional)
                    </label>
                    <textarea
                      data-testid="booking-notes-input"
                      value={form.notes}
                      onChange={set("notes")}
                      rows={3}
                      placeholder="A little about your business and what you'd like to delegate…"
                      className="bg-transparent py-2.5 outline-none text-base resize-none placeholder:opacity-40"
                      style={{ borderBottom: "1px solid var(--border-light)", color: "var(--text-primary)" }}
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="booking-confirm-button"
                    disabled={loading}
                    className="self-start inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-medium transition-opacity duration-300 disabled:opacity-60"
                    style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}
                  >
                    {loading ? "Confirming…" : "Confirm booking"}
                    {!loading && <ArrowRight size={16} />}
                  </button>
                </form>
              )}

              {step === 4 && confirmed && (
                <div data-testid="booking-step-confirmed" className="text-center py-4">
                  <span
                    className="mx-auto grid place-items-center w-16 h-16 rounded-full mb-5"
                    style={{ backgroundColor: "var(--accent-gold)" }}
                  >
                    <Check size={30} color="#2E241D" />
                  </span>
                  <h3 className="font-serif text-2xl mb-2" style={{ color: "var(--text-primary)" }}>
                    You're booked!
                  </h3>
                  <p className="text-base mb-1" style={{ color: "var(--text-primary)" }}>
                    {prettyDate(date)} · {to12h(time)} ET
                  </p>
                  <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
                    A confirmation is on its way to {confirmed.email}. Add it to your calendar:
                  </p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <a
                      href={confirmed.google_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="booking-add-google"
                      className="rounded-full px-6 py-3 text-sm font-medium"
                      style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}
                    >
                      Add to Google Calendar
                    </a>
                    <a
                      href={confirmed.ics_url}
                      data-testid="booking-add-ics"
                      className="rounded-full px-6 py-3 text-sm font-medium"
                      style={{ backgroundColor: "var(--accent-gold)", color: "#2E241D" }}
                    >
                      Add to Apple / Outlook
                    </a>
                  </div>
                  <button
                    onClick={closeBooking}
                    data-testid="booking-done-button"
                    className="mt-6 text-sm link-underline"
                    style={{ color: "var(--rose-mocha-deep)" }}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
